import { useState, useRef, useEffect } from 'react';
import type { StrategyTemplate, ActiveStrategyReference, StrategySettings } from '../../types/strategy';
import { PREMADE_TEMPLATES } from '../../constants/premadeTemplates';
import { ConfirmModal } from '../ConfirmModal';
import { SaveTemplateForm } from './SaveTemplateForm';
import { COLORS, INPUT_STYLE } from '../../constants/theme';

interface TemplatesPanelProps {
  templates: StrategyTemplate[];
  activeStrategyRef: ActiveStrategyReference | null;
  onLoadTemplate: (settings: StrategySettings) => void;
  onActivateCustom: (planId: string) => Promise<void>;
  onActivatePremade: (premadeKey: string) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onSaveNew: (name: string) => Promise<void>;
  onSaveChanges: (planId: string) => Promise<void>;
  onRename: (planId: string, newName: string) => Promise<void>;
  onDelete: (planId: string) => Promise<void>;
  isDirty: boolean;
  currentTemplateId: string | null;
  /** SF-172: when true, the trigger shows "Loading templates…" instead of "No template" — this
   *  component owns no fetch itself, so the page (`Strategy.tsx`'s `templatesLoading`) drives it. */
  loading?: boolean;
}

export function TemplatesPanel({
  templates,
  activeStrategyRef,
  onLoadTemplate,
  onActivateCustom,
  onActivatePremade,
  onDeactivate,
  onSaveNew,
  onSaveChanges,
  onRename,
  onDelete,
  isDirty,
  currentTemplateId,
  loading = false,
}: TemplatesPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isActive = (type: string, key: string) =>
    activeStrategyRef?.sourceType === type && activeStrategyRef?.sourceId === key;

  const activeTemplateName = (() => {
    if (!activeStrategyRef) return null;
    if (activeStrategyRef.sourceType === 'custom') {
      return templates.find((t) => t.id === activeStrategyRef.sourceId)?.name ?? null;
    }
    if (activeStrategyRef.sourceType === 'premade') {
      return PREMADE_TEMPLATES.find((t) => t.key === activeStrategyRef.sourceId)?.name ?? null;
    }
    return null;
  })();

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // SF-172: Escape closes the popover and returns focus to the trigger.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleSaveNew = async (name: string) => {
    setSaving(true);
    try {
      await onSaveNew(name);
      setShowSaveInput(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (planId: string) => {
    if (!editName.trim()) return;
    await onRename(planId, editName.trim());
    setEditingId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    await onDelete(deleteTargetId);
    setDeleteTargetId(null);
  };

  const deleteTargetName = deleteTargetId
    ? (templates.find((t) => t.id === deleteTargetId)?.name ?? 'this template')
    : '';

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Compact trigger button */}
        <button
          ref={triggerRef}
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center gap-2 px-3 py-2.5 rounded text-sm border transition-colors"
          style={{
            minHeight: 44,
            backgroundColor: COLORS.card,
            borderColor: COLORS.border,
            color: COLORS.text.secondary,
          }}
          aria-expanded={isOpen}
          aria-controls="templates-dropdown"
        >
          <span className="truncate max-w-[160px]">
            {loading ? 'Loading templates…' : (activeTemplateName ?? 'No template')}
          </span>
          {isDirty && currentTemplateId && (
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" title="Unsaved changes" />
          )}
          <span className="text-xs" style={{ color: COLORS.text.muted }}>▾</span>
        </button>

        {/* Dropdown popover */}
        {isOpen && (
          <div
            id="templates-dropdown"
            role="menu"
            className="absolute right-0 top-full mt-1 z-50 rounded-lg p-3 space-y-3 w-72 max-w-full max-h-[60vh] overflow-y-auto shadow-lg"
            style={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
            }}
          >
            {/* Premade Templates */}
            <div>
              <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: COLORS.text.tertiary }}>Premade</div>
              <div className="flex flex-wrap gap-1">
                {PREMADE_TEMPLATES.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => {
                      onLoadTemplate(t.settings);
                      onActivatePremade(t.key);
                      setIsOpen(false);
                    }}
                    className={`px-2 py-1 rounded text-xs border transition-colors ${
                      isActive('premade', t.key)
                        ? 'bg-blue-700 border-blue-500'
                        : 'hover:border-[#2a3448]'
                    }`}
                    style={isActive('premade', t.key)
                      ? { color: COLORS.text.primary }
                      : { backgroundColor: COLORS.card, borderColor: COLORS.border, color: COLORS.text.secondary }
                    }
                    title={t.description}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Templates */}
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: '0.5rem' }}>
              <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: COLORS.text.tertiary }}>Custom</div>
              <div className="space-y-1">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-center gap-1">
                    {editingId === t.id ? (
                      <div className="flex items-center gap-1 flex-1">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleRename(t.id)}
                          className="text-xs px-2 py-1 rounded flex-1"
                          style={{ ...INPUT_STYLE, border: `1px solid ${COLORS.inputBorder}` }}
                          autoFocus
                        />
                        <button onClick={() => handleRename(t.id)} className="text-green-400 text-xs">✓</button>
                        <button onClick={() => setEditingId(null)} className="text-xs" style={{ color: COLORS.text.tertiary }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            onLoadTemplate(t.settings);
                            onActivateCustom(t.id);
                            setIsOpen(false);
                          }}
                          className={`flex-1 text-left px-2 py-1 rounded text-xs border transition-colors ${
                            isActive('custom', t.id)
                              ? 'bg-blue-700 border-blue-500'
                              : 'hover:border-[#2a3448]'
                          }`}
                          style={isActive('custom', t.id)
                            ? { color: COLORS.text.primary }
                            : { backgroundColor: COLORS.card, borderColor: COLORS.border, color: COLORS.text.secondary }
                          }
                        >
                          {t.name}
                        </button>
                        <button
                          onClick={() => { setEditingId(t.id); setEditName(t.name); }}
                          className="text-xs hover:opacity-80"
                          style={{ color: COLORS.text.muted }}
                          title="Rename"
                          aria-label={`Rename ${t.name}`}
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => setDeleteTargetId(t.id)}
                          className="text-xs hover:text-red-400"
                          style={{ color: COLORS.text.muted }}
                          title="Delete"
                          aria-label={`Delete ${t.name}`}
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                ))}

                {/* Save New */}
                {showSaveInput ? (
                  <SaveTemplateForm
                    onSave={handleSaveNew}
                    onCancel={() => setShowSaveInput(false)}
                    saving={saving}
                  />
                ) : (
                  <button
                    onClick={() => setShowSaveInput(true)}
                    className="w-full px-2 py-1 rounded text-xs border border-dashed text-left hover:border-[#2a3448]"
                    style={{ borderColor: COLORS.border, color: COLORS.text.muted }}
                    disabled={templates.length >= 10}
                    title={templates.length >= 10 ? 'Maximum 10 templates' : 'Save current settings as template'}
                  >
                    + Save as Template
                  </button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2" style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: '0.5rem' }}>
              {isDirty && currentTemplateId && (
                <button
                  onClick={() => { onSaveChanges(currentTemplateId); setIsOpen(false); }}
                  className="px-2 py-1 rounded text-xs bg-green-700 hover:bg-green-600"
                  style={{ color: COLORS.text.primary }}
                >
                  Save Changes
                </button>
              )}
              {activeStrategyRef && (
                <button
                  onClick={() => { onDeactivate(); setIsOpen(false); }}
                  className="px-2 py-1 rounded text-xs hover:brightness-125"
                  style={{ backgroundColor: COLORS.card, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
                >
                  Deactivate
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteTargetId !== null}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
        title="Delete Template"
        confirmText="Delete"
        cancelText="Cancel"
      >
        Delete &ldquo;{deleteTargetName}&rdquo;? This cannot be undone.
      </ConfirmModal>
    </>
  );
}
